import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import clsx from 'clsx';
import {
  Divider,
  Grid,
  Paper,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Tabs,
  Tab,
  FormHelperText,
  CircularProgress,
  ListItemText,
  Typography,
  Tooltip,
} from '@material-ui/core';

import { SectionHeader, Select, CheckBox } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { PlansContext } from 'Views/Plans/context';
import {
  PRODUCT_INFO_UPDATED,
  UPDATE_FIELDS_VALIDITY,
  PLAN_UPDATED,
} from 'Views/Plans/context/actions';
import { PLAN_PACKAGE_TYPE, STATUS_LIST } from 'Views/Common/enum';
import { CategoryDiscounts, CPTValues } from '../';
import { SUBSCRIPTION_TYPE } from 'global-constants/constants';
import { PLAN_SERVICES } from 'Services';
import Utils from 'Shared/Utils';
import useStyles from './styles';
import SubscriptionPaymentCodes from '../SubscriptionPayments';
import OpenEnrollmentFields from './OpenEnrollmentFields';
import {
  OPEN_ENROLLMENT_FIELDS,
  SET_OPEN_ENROLLMENT_LABEL,
  getDayAfter,
  getOpenEnrollmentError,
  isOpenEnrollmentOn,
  toSavedOpenEnrollmentValue,
} from 'Views/Plans/utils/openEnrollmentHelpers';

const PLAN_ATTR_PRIVATE_ONLY = 'private_only';
const PLAN_ATTR_PORTAL_VIEW_ONLY = 'patient_portal_view_only';

const ProductInfo = (props) => {
  const { dispatch: globalDispatch } = useContext(Context);
  const {
    state: {
      productInfo,
      selectedGroup,
      highlightErrors,
      has_subscriptions,
      plans,
      plan_id,
    },
    dispatch,
  } = useContext(PlansContext);
  const { setExecuteHandleSubmit, planAttributes: planAttr } = props || {};

  let {
    plan_name,
    plan_package_type,
    member_types,
    description,
    statement_descriptor,
    allowed_age_max,
    allowed_age_min,
    additional_members,
    display_order = '',
    allowFutureDateToggle,
    renewal_plan_pkg_id,
    plan_status,
    disclosure_text = '',
    annual_savings_disclosure_text = '',
    pricing_tiers_list = [],
    is_onetime_plan,
    enrollment_start_day,
    enrollment_end_day,
    is_auto_renew,
  } = productInfo;
  const isOpenEnrollment = isOpenEnrollmentOn(productInfo);
  const billingFrequencies = plans.map(
    (plan) => plan.plan_details?.billing_frequency,
  );
  const openEnrollmentError = getOpenEnrollmentError(
    productInfo,
    billingFrequencies,
  );

  const PLAN_ATTRIBUTES = planAttr?.reduce(
    (acc, item) => {
      if (item.custom_value) {
        acc[item.custom_value] = item.display_name;
      } else {
        acc[item.name] = item.display_name;
      }
      return acc;
    },
    // SAP-8323: the screen-only switch isn't in the saved attributes.
    { [OPEN_ENROLLMENT_FIELDS.enabled]: SET_OPEN_ENROLLMENT_LABEL },
  );

  const vet_only = selectedGroup.industry === 'Veterinary';
  const classes = useStyles();

  const isAllPricePrivateOnly =
    plans.length > 0 &&
    plans.every((plan) => plan.plan_details?.private_only);
  const isAllPricePortalViewOnly =
    plans.length > 0 &&
    plans.every((plan) => plan.plan_details?.patient_portal_view_only);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansList, setPlansList] = useState([]);
  const [renewalPlanId] = useState(renewal_plan_pkg_id);
  const [planAttributes, setPlanAttributes] = useState([]);
  const [upgradePlansList, setUpgradePlansList] = useState([]);
  const [upgradePlansLoading, setUpgradePlansLoading] = useState(false);
  const memberTypeSelected = planAttributes.some(
    (attrs) =>
      (attrs.name === SUBSCRIPTION_TYPE.CUSTOMER.value ||
        attrs.name === SUBSCRIPTION_TYPE.EMPLOYEE.value) &&
      attrs.value === true,
  );

  const getPlanAttributesValue = (name, customValue) => {
    if (name === PLAN_ATTR_PRIVATE_ONLY) {
      return isAllPricePrivateOnly;
    }
    if (name === PLAN_ATTR_PORTAL_VIEW_ONLY) {
      return isAllPricePortalViewOnly;
    }
    if (name === OPEN_ENROLLMENT_FIELDS.enabled) {
      return isOpenEnrollment;
    }
    if (name === 'legacy_reactivation_allowed') {
      return (
        productInfo?.legacy_reactivation_allowed ??
        pricing_tiers_list.some(
          (pricingTier) => pricingTier.legacy_reactivation_allowed,
        )
      );
    }
    return productInfo[name] ?? customValue;
  };

  useEffect(() => {
    const newPlanAttributes = _.cloneDeep(planAttr);
    const customPlanAttributes = newPlanAttributes?.map((value) => ({
      name: value.name === 'member_types' ? value.custom_value : value.name,
      value:
        value.name === 'member_types'
          ? member_types?.includes(value.custom_value)
          : getPlanAttributesValue(value.name, value.default_value),
      display_name: value.display_name,
    }));
    // SAP-8323: a screen-only switch, not one of the saved attributes.
    customPlanAttributes?.push({
      name: OPEN_ENROLLMENT_FIELDS.enabled,
      value: isOpenEnrollment,
      display_name: SET_OPEN_ENROLLMENT_LABEL,
    });
    setPlanAttributes(customPlanAttributes);
  }, [productInfo, isAllPricePrivateOnly, isAllPricePortalViewOnly, planAttr]);

  useEffect(() => {
    const infoFields = { plan_name, description, statement_descriptor };
    const isFieldsError =
      Boolean(openEnrollmentError) ||
      Object.keys(infoFields).some((key) => {
        if (!infoFields[key]) {
          return true;
        }

        return false;
      });

    dispatch({
      type: UPDATE_FIELDS_VALIDITY,
      payload: {
        isValid: { product: !isFieldsError },
      },
    });
  }, [highlightErrors, productInfo, plans]);

  useEffect(() => {
    dispatch({
      type: UPDATE_FIELDS_VALIDITY,
      payload: {
        isValid: { memberTypeSelected: memberTypeSelected },
      },
    });
  }, [highlightErrors, memberTypeSelected]);

  const dispatchProductInfoChange = (updatedProductInfo) =>
    dispatch({
      type: PRODUCT_INFO_UPDATED,
      payload: { productInfo: updatedProductInfo },
    });

  // Clear renewal plan selection when plan is one-time (one-time plans cannot be renewed)
  useEffect(() => {
    if (is_onetime_plan && renewal_plan_pkg_id) {
      dispatchProductInfoChange({
        ...productInfo,
        renewal_plan_pkg_id: '',
      });
    }
  }, [is_onetime_plan, renewal_plan_pkg_id]);

  const dispatchUpdatedPlans = (updatedPlans) =>
    dispatch({
      type: PLAN_UPDATED,
      payload: {
        plans: updatedPlans,
      },
    });

  const handlePrivateOnlyChange = (private_only) => {
    if (private_only && isAllPricePortalViewOnly) {
      dispatchGlobalNotification(
        'error',
        'Visible In-Office Only cannot be enabled while Patient Portal View Only is active.',
      );
      return;
    }

    let updatedPlans = _.cloneDeep(plans);
    updatedPlans.forEach((plan) => {
      plan.plan_details.private_only = private_only;
      if (private_only) {
        plan.plan_details.patient_portal_view_only = false;
      }
    });
    dispatchUpdatedPlans(updatedPlans);
  };

  const handlePortalViewOnlyChange = (patient_portal_view_only) => {
    if (patient_portal_view_only && isAllPricePrivateOnly) {
      dispatchGlobalNotification(
        'error',
        'Patient Portal View Only cannot be enabled while Visible In-Office Only is active.',
      );
      return;
    }

    let updatedPlans = structuredClone(plans);
    updatedPlans.forEach((plan) => {
      plan.plan_details.patient_portal_view_only = patient_portal_view_only;
      if (patient_portal_view_only) {
        plan.plan_details.private_only = false;
      }
    });
    dispatchUpdatedPlans(updatedPlans);
  };

  const handleMemberTypeChange = (memberType, actionType = 'add') => {
    let newValue = [];

    const planSubTypes = _.cloneDeep(member_types);

    if (actionType === 'add') {
      newValue = [...planSubTypes, memberType];
    } else {
      newValue = planSubTypes.filter((memType) => memType !== memberType);
    }
    handleChange('member_types', newValue);
  };

  const handleDateChange = (selDate, name) => handleChange(name, selDate);

  const handleStatusChange = (value) => {
    let updatedPlans = _.cloneDeep(plans);
    updatedPlans.forEach((plan) => (plan.plan_details.status = value));
    dispatchUpdatedPlans(updatedPlans);
  };

  const handleChange = (name, value) => {
    let dependentValues = {};
    if (name === 'plan_status') handleStatusChange(value);
    
    if (name === 'plan_package_type' && value !== 'Base')
      dependentValues = {
        addon_allowed: false,
      };

    // One-time plans never renew (SAP-8323); an unset Auto Renew stays
    // unset.
    if (name === 'is_onetime_plan' && value && is_auto_renew)
      dependentValues = {
        ...dependentValues,
        [OPEN_ENROLLMENT_FIELDS.autoRenew]: false,
      };

    dispatchProductInfoChange({
      ...productInfo,
      ...dependentValues,
      [name]: value,
    });
  };



  // SAP-8323: turning Set Open Enrollment off clears its row, so the plan
  // keeps the member type's renewal default. Turning it on leaves Auto
  // Renew not set until it's chosen.
  const handleOpenEnrollmentToggle = (enabled) =>
    dispatchProductInfoChange({
      ...productInfo,
      [OPEN_ENROLLMENT_FIELDS.enabled]: enabled,
      ...(!enabled && {
        [OPEN_ENROLLMENT_FIELDS.startDay]: null,
        [OPEN_ENROLLMENT_FIELDS.endDay]: null,
        [OPEN_ENROLLMENT_FIELDS.autoRenew]: null,
      }),
    });

  // An End Date fills the Start Date with the day after it, while the Start
  // Date is empty or still the one filled in from the previous End Date.
  const handleOpenEnrollmentChange = (name, value) => {
    const startIsAutoFilled =
      !enrollment_start_day ||
      enrollment_start_day === getDayAfter(enrollment_end_day);
    const fill =
      name === OPEN_ENROLLMENT_FIELDS.endDay && value && startIsAutoFilled
        ? { [OPEN_ENROLLMENT_FIELDS.startDay]: getDayAfter(value) }
        : {};
    dispatchProductInfoChange({
      ...productInfo,
      ...fill,
      [name]: toSavedOpenEnrollmentValue(name, value),
    });
  };

  const handleClearOpenEnrollmentDates = () =>
    dispatchProductInfoChange({
      ...productInfo,
      [OPEN_ENROLLMENT_FIELDS.startDay]: null,
      [OPEN_ENROLLMENT_FIELDS.endDay]: null,
    });

  const handleMultiSelectChange = (name, value) => {
    if (name === OPEN_ENROLLMENT_FIELDS.enabled) {
      handleOpenEnrollmentToggle(value);
      return;
    }
    if (name === PLAN_ATTR_PRIVATE_ONLY) {
      handlePrivateOnlyChange(value);
      return;
    }
    if (name === PLAN_ATTR_PORTAL_VIEW_ONLY) {
      handlePortalViewOnlyChange(value);
      return;
    }
    if (
      name.includes(SUBSCRIPTION_TYPE.CUSTOMER.value) ||
      name.includes(SUBSCRIPTION_TYPE.EMPLOYEE.value)
    ) {
      const actionType = value ? 'add' : 'delete';
      handleMemberTypeChange(name, actionType);
    } else {
      if (checkIfChangeAllowed(name)) {
        handleChange(name, value);
      } else {
        dispatchGlobalNotification(
          'error',
          'Allow Future Date value cannot be changed',
        );
      }
    }
  };

  const checkIfChangeAllowed = (name) => {
    if (name !== 'allow_future_dated') return true;

    return allowFutureDateToggle;
  };

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const renderInputField = (
    value,
    name,
    label,
    textFieldProps = {},
    defaultValue = 0,
  ) => {
    const { inputProps, labelProps, ...restProps } = textFieldProps;

    return (
      <TextField
        fullWidth
        required
        name={name}
        id={name}
        label={label}
        value={value}
        error={Boolean(highlightErrors && !value)}
        helperText={`${label} is required`}
        variant="outlined"
        onChange={(event) => {
          let { name: eName, value: eValue = '' } = event?.target || {};
          inputProps?.type === 'number' &&
            (eValue = Math.round(parseFloat(eValue)) || defaultValue);

          handleChange(eName, eValue);
        }}
        inputProps={{
          'data-testid': `product-info-${name}`,
          ...inputProps,
        }}
        onKeyDown={(e) => {
          if (value === 0) {
            const input = e.target;
            input.select();
          }
        }}
        InputLabelProps={{
          ...labelProps,
        }}
        {...restProps}
      />
    );
  };

  const renderTitle = () => (
    <Grid container className={classes.tabsContainer}>
      <Tabs
        value={plan_package_type}
        indicatorColor="secondary"
        textColor="secondary"
        onChange={(_event, newValue) =>
          handleChange('plan_package_type', newValue)
        }
        variant="scrollable"
        scrollButtons="auto"
        className={classes.planTitleTabs}
      >
        {Object.keys(PLAN_PACKAGE_TYPE).map((indKey) => (
          <Tab
            key={indKey}
            value={indKey}
            label={PLAN_PACKAGE_TYPE[indKey]}
            disabled={props?.planPackageId}
          />
        ))}
      </Tabs>
    </Grid>
  );

  const renderDropdownField = (
    value,
    name,
    label,
    selectList,
    textFieldProps = {},
  ) => {
    const { helperText, ...restProps } = textFieldProps;

    return (
      <>
        <FormControl fullWidth variant="outlined" {...restProps}>
          <InputLabel id="product-renewal-plan-label"> {label} </InputLabel>
          <Select
            id={`product-select-${name}`}
            name={name}
            value={value}
            onChange={(evt) => handleChange(name, evt?.target?.value)}
            label={`${label} ${restProps.required ? '*' : ''}`}
          >
            {selectList.map((indData, index) => (
              <MenuItem
                key={indData.key}
                value={indData.key}
                data-testid={`product-select-${name}-item-${index}`}
              >
                {indData.label}
              </MenuItem>
            ))}
          </Select>
          {helperText && <FormHelperText> {helperText} </FormHelperText>}
        </FormControl>

        {plansLoading && (
          <CircularProgress
            size={20}
            thickness={5}
            className="dropdown-loader"
          />
        )}
      </>
    );
  };

  const handleResponse = (response) => {
    const rows = Utils.getValue(response?.data?.rows, []);
    let filteredRows = [];

    rows.forEach((row) => {
      if (!plan_id || row.plan_package_id !== plan_id)
        filteredRows.push({
          key: row?.plan_package_id,
          label: row?.plan_name,
        });
    });

    setPlansList(filteredRows);
  };

  const fetchPlans = async (group_id) => {
    if (!group_id || group_id === 'ALL_GROUPS') return;

    setPlansLoading(true);

    try {
      const response = await PLAN_SERVICES.fetchGroupPlanPackages(group_id);

      Utils.checkIfSuccess(response) && handleResponse(response);
    } catch (err) {
      console.log(err);
    }

    setPlansLoading(false);
  };

  const fetchUpgradePlans = async (group_id, maxAge) => {
    if (!group_id || group_id === 'ALL_GROUPS' || maxAge === undefined || maxAge === null || maxAge <= 0) {
      setUpgradePlansList([]);
      return;
    }

    setUpgradePlansLoading(true);

    try {
      const response = await PLAN_SERVICES.fetchGroupPlanPackages(group_id);

      if (Utils.checkIfSuccess(response) && response?.data?.rows) {
        // Show plans with no age limits (0 or undefined) OR plans with higher age limits
        const filteredPlans = response.data.rows.filter(plan => 
          !plan.allowed_age_max || plan.allowed_age_max === 0 || plan.allowed_age_max > maxAge
        );
        
        const upgradePlansOptions = filteredPlans.map(plan => ({
          key: plan?.plan_package_id,
          label: plan?.plan_name,
        }));
        
        setUpgradePlansList([
          { key: '', label: 'None' },
          ...upgradePlansOptions,
        ]);
      }
    } catch (err) {
      console.log(err);
    }

    setUpgradePlansLoading(false);
  };

  const showSelectedPlanAttributes = (selected) => {
    const valueSelected = selected.filter((attr) => attr.value);
    const selectedItems = valueSelected
      .map((value) => PLAN_ATTRIBUTES[value.name])
      .join(', ');
    const count = valueSelected.length;
    if (selectedItems.length <= 48) return selectedItems;

    const overflowCount = (
      <Typography
        component="span"
        variant="body1"
        className={classes.showMoreTextColor}
      >
        +{count - 2} more
      </Typography>
    );
    const firstCommaOccurance = selectedItems.indexOf(',');
    const SecondOccurance = selectedItems.indexOf(',', firstCommaOccurance + 1);
    const trimmedValues = selectedItems.slice(0, SecondOccurance);

    return (
      <>
        {trimmedValues} {overflowCount}
      </>
    );
  };

  useEffect(() => {
    selectedGroup?.group_id && fetchPlans(selectedGroup?.group_id);
  }, [selectedGroup?.group_id]);

  useEffect(() => {
    // Fetch upgrade plans when max age changes
    if (selectedGroup?.group_id && allowed_age_max !== undefined && allowed_age_max !== null && allowed_age_max > 0) {
      fetchUpgradePlans(selectedGroup?.group_id, allowed_age_max);
    } else {
      setUpgradePlansList([]);
    }
  }, [selectedGroup?.group_id, allowed_age_max]);

  return (
    <>
      <Paper
        className={clsx('widget-wrapper', classes.widgetContainer)}
        elevation={0}
      >
        <SectionHeader
          title={renderTitle()}
          titleVariant="h5"
          titleComponent="h5"
          gutterBottom
          rightSection={renderInputField(
            display_order,
            'display_order',
            'Display Order',
            {
              inputProps: {
                type: 'number',
                onwheel: (e) => e.target.blur(),
                min: 1,
              },
              error: false,
              required: false,
              helperText: '',
              color: 'secondary',
            },
            '',
          )}
        />
        <Divider />

        <Grid container spacing={3} id="product-info">
          <Grid item xs={12}>
            <Grid container spacing={3} className="gutter">
              <Grid item xs={12} md={6} lg={4}>
                {renderInputField(
                  statement_descriptor,
                  'statement_descriptor',
                  'Statement Description',
                  {
                    inputProps: {
                      maxLength: 22,
                    },
                    helperText: `Choose something your customers will recognize on a bank statement`,
                  },
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderInputField(plan_name, 'plan_name', 'Product Name', {
                  helperText:
                    Boolean(highlightErrors && !plan_name) &&
                    'Product Name is required',
                })}
              </Grid>

              {vet_only && (
                <Grid item xs={12} md={6} lg={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="pet-type-label">Pet Type</InputLabel>
                    <Select
                      id="pet-type-select"
                      name="pet-type"
                      label="Pet Type *"
                    >
                      <MenuItem value={0}>Dog</MenuItem>
                      <MenuItem value={0}>Cat</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6} lg={4}>
                {renderInputField(
                  description,
                  'description',
                  'Product Description',
                  {
                    helperText: Boolean(highlightErrors && !description)
                      ? 'Product Description is required'
                      : 'Product description appears at checkout, in the customer portal, and on quotes',
                  },
                )}
              </Grid>
              {plan_package_type === 'Tiered' && (
                <Grid item xs={12} md={6} lg={6}>
                  {renderInputField(
                    additional_members,
                    'additional_members',
                    'Allowed Additional Members',
                    {
                      inputProps: {
                        type: 'number',
                        onwheel: (e) => e.target.blur(),
                        min: 0,
                      },
                      helperText: '',
                      error: false,
                      required: false,
                    },
                  )}
                </Grid>
              )}

              {plan_package_type !== 'Tiered' && (
                <>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderInputField(
                      allowed_age_min || 0,
                      'allowed_age_min',
                      'Allowed Minimum Age',
                      {
                        inputProps: {
                          type: 'number',
                          onwheel: (e) => e.target.blur(),
                          min: 0,
                        },
                        error: false,
                        required: false,
                        helperText:
                          'Allowed minimum age of the customer for this plan',
                      },
                      '',  // Pass empty string as defaultValue
                    )}
                  </Grid>

                  <Grid item xs={12} md={6} lg={4}>
                    {renderInputField(
                      allowed_age_max || 0,
                      'allowed_age_max',
                      'Allowed Maximum Age',
                      {
                        inputProps: {
                          type: 'number',
                          onwheel: (e) => e.target.blur(),
                          min: 0,
                        },
                        error: false,
                        required: false,
                        helperText:
                          'Allowed maximum age of the customer for this plan',
                      },
                      '',  // Pass empty string as defaultValue
                    )}
                  </Grid>
                </>
              )}

              <Grid
                item
                xs={12}
                md={6}
                lg={plan_package_type === 'Tiered' ? 6 : 4}
              >
                {renderDropdownField(
                  plan_status,
                  'plan_status',
                  'Plan Status',
                  STATUS_LIST,
                  {
                    required: true,
                    helperText: 'Hide plans if expired',
                    // disabled: (plan_id && plan_status === 'Hidden')
                  },
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={allowed_age_max && allowed_age_max > 0 ? 4 : 6} className="position-relative">
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    id="multiple-checkbox-label"
                    className={clsx(
                      highlightErrors && !memberTypeSelected
                        ? 'color_error'
                        : '',
                    )}
                  >
                    Plan Attributes
                  </InputLabel>
                  <Select
                    id="multiple-checkbox-label"
                    className={classes.multiSelect}
                    multiple
                    value={planAttributes}
                    label="Plan Attributes"
                    error={Boolean(highlightErrors && !memberTypeSelected)}
                    renderValue={showSelectedPlanAttributes}
                  >
                    {planAttributes.length &&
                      planAttributes.map(({ name, value, display_name }) => {
                        const isOnetimePlan = name?.includes('is_onetime_plan');
                        const disableOnetimeOption = isOnetimePlan && has_subscriptions;

                        if (disableOnetimeOption) {
                          return (
                            <Tooltip
                              key={name}
                              title="Cannot modify. Plan has active or historical subscribers."
                              placement="right"
                            >
                              <span style={{ display: 'block' }}>
                                <MenuItem disabled style={{ opacity: 0.6 }}>
                                  <CheckBox checked={value} disabled={true}/>
                                  <ListItemText primary={display_name} />
                                </MenuItem>
                              </span>
                            </Tooltip>
                          );
                        }

                        return (
                          <MenuItem
                            key={name}
                            value={value}
                            onClick={() => {
                              handleMultiSelectChange(name, !value);
                            }}
                          >
                            <CheckBox checked={value} />
                            <ListItemText primary={display_name} />
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {highlightErrors && !memberTypeSelected ? (
                    <FormHelperText className="color_error">
                      One of the Member Type should be selected
                    </FormHelperText>
                  ) : (
                    ''
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={allowed_age_max && allowed_age_max > 0 ? 4 : 6} className="position-relative">
                {renderDropdownField(
                  renewal_plan_pkg_id,
                  'renewal_plan_pkg_id',
                  'Renewal Product Name',
                  plansList,
                  {
                    helperText:
                      is_onetime_plan ? 'One-time plan cannot be renewed' : 'Plan to upgrade after the current plan expires',
                    disabled:
                      plansLoading ||
                      is_onetime_plan ||
                      (plan_id && renewalPlanId && has_subscriptions),
                  },
                )}
              </Grid>

                {/* Only show Renewal Max Age Plan dropdown if maximum age is > 0 */}
                {allowed_age_max !== undefined && allowed_age_max !== null && allowed_age_max > 0 && (
                  <Grid item xs={12} md={6} lg={4} className="position-relative">
                    {renderDropdownField(
                    productInfo.renewal_max_age_plan_pkg_id || '',
                    'renewal_max_age_plan_pkg_id',
                    'Upgrade Plan Name',
                    upgradePlansList,
                    {
                      helperText:
                        'Plan subscribers will be upgraded to when they exceed the maximum allowed age at renewal',
                      disabled: upgradePlansLoading,
                    },
                  )}
                    {upgradePlansLoading && (
                      <CircularProgress
                        size={20}
                        thickness={5}
                        className="dropdown-loader"
                      />
                    )}
                  </Grid>
                )}

              {isOpenEnrollment && (
                <Grid item xs={12}>
                  <OpenEnrollmentFields
                    startDay={enrollment_start_day || ''}
                    endDay={enrollment_end_day || ''}
                    autoRenew={is_auto_renew ?? null}
                    isOnetimePlan={Boolean(is_onetime_plan)}
                    error={openEnrollmentError}
                    showError={Boolean(
                      highlightErrors ||
                        (enrollment_start_day && enrollment_end_day),
                    )}
                    onChange={handleOpenEnrollmentChange}
                    onClearDates={handleClearOpenEnrollmentDates}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                {renderInputField(
                  annual_savings_disclosure_text,
                  'annual_savings_disclosure_text',
                  'Annual Savings Disclosure',
                  {
                    error: false,
                    required: false,
                    helperText: '',
                    label: 'Annual Savings Disclosure',
                    placeholder:
                      'e.g. Save $89 a year vs paying monthly.',
                    multiline: true,
                    minRows: 3,
                    maxRows: 10,
                  },
                )}
              </Grid>

              <Grid item xs={12}>
                {renderInputField(
                  disclosure_text,
                  'disclosure_text',
                  'Disclosure',
                  {
                    error: false,
                    required: false,
                    helperText: '',
                    label: 'Disclosure',
                    multiline: true,
                    minRows: 3,
                    maxRows: 10,
                  },
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper
        className={clsx('widget-wrapper', classes.widgetContainer)}
        elevation={0}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CPTValues />
          </Grid>
        </Grid>
      </Paper>
      {selectedGroup?.group_id &&
        (plan_package_type === 'Base' || plan_package_type === 'Tiered') && (
          <Paper
            className={clsx('widget-wrapper', classes.widgetContainer)}
            elevation={0}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CategoryDiscounts
                  level={'group'}
                  setExecuteHandleSubmit={setExecuteHandleSubmit}
                  levelId={selectedGroup?.group_id}
                  planPackageId={props?.planPackageId}
                  showCategoryDiscounts={Utils.isDentalIndustry(
                    selectedGroup?.industry,
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        )}
      <Paper
        className={clsx('widget-wrapper', classes.widgetContainer)}
        elevation={0}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SubscriptionPaymentCodes
              level={'group'}
              setExecuteHandleSubmit={setExecuteHandleSubmit}
              levelId={selectedGroup?.group_id}
              planPackageId={props?.planPackageId}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

ProductInfo.propTypes = {
  planPackageId: PropTypes.string,
  planAttributes: PropTypes.array,
};

export default ProductInfo;
